import { ComponentChildren, FunctionalComponent } from 'preact';
import clsx from 'clsx';
import { Children, memo } from 'preact/compat';
import { wrapWithComponent } from 'utilities/components';
import { variationName } from 'utilities/css';

import { Item } from './Item';

import styles from './Stack.module.scss';

type Spacing = 'none' | 'tight' | 'extraTight' | 'loose' | 'extraLoose';

type Distribution =
  | 'leading'
  | 'trailing'
  | 'center'
  | 'equalSpacing'
  | 'fill'
  | 'fillEvenly';

type Alignment = 'leading' | 'trailing' | 'center' | 'fill' | 'baseline';

interface StackProps {
  children: ComponentChildren;
  vertical?: boolean;
  spacing?: Spacing;
  distribution?: Distribution;
  alignment?: Alignment;
  wrap?: boolean | 'reverse';
  classNames?: string[];
}

export const Stack = memo(function Stack({
  children,
  vertical,
  spacing,
  distribution,
  alignment,
  wrap,
  classNames = [],
}: StackProps) {
  const className = clsx(
    styles.Stack,
    vertical && styles.vertical,
    spacing && styles[variationName('spacing', spacing)],
    distribution && styles[variationName('distribution', distribution)],
    alignment && styles[variationName('alignment', alignment)],
    wrap === false && styles.noWrap,
    wrap === 'reverse' && styles.wrapReverse,
    ...classNames,
  );

  const childrenMarkup = Children.toArray(children).map((child, index) => {
    const props = { key: index };

    return wrapWithComponent(child, Item, props);
  });

  return <div class={className}>{childrenMarkup}</div>;
}) as FunctionalComponent<StackProps> & {
  Item: typeof Item;
};

Stack.Item = Item;
